import * as React from 'react';
import { Badge, Col, Row } from 'reactstrap';
import { AccentButton } from '../controls/AccentButton';
import { AccentCheckUnbound } from '../controls/unbound/AccentCheckUnbound';
import { AccentTextUnbound } from '../controls/unbound/AccentTextUnBound';
import { downloadObject, queryNoTracking } from '../services/DataService';
import { accentUtils, from, showWait, t } from '../services/HelperService';
import { configFrom, loadProductModel, productVersionSort, removeFromModelCache } from '../services/ProductService';
import { documentTemplateTypes, printSettingItemShowAs } from '../services/TemplateService';
import { AccentDialog } from './AccentDialog';

const getExportProductKey = (product) => `${product.ID}_${product.VersionID ?? accentUtils.getEmptyGuid()}`;
const query_PrintSettings_ExportRaw = queryNoTracking("PrintSettingsExportRaw");

const getProductCode = (product) => product.ProductCode ?? product.Code ?? "";

const toKey = (value) => `${value}`;

const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const exportSchema = "insyte.document-template-export.v1";

const documentTypeGroups = [
    { type: documentTemplateTypes.quote, includePrintSettings: true },
    { type: documentTemplateTypes.orderConfirmation, includePrintSettings: true },
    { type: documentTemplateTypes.invoice, includePrintSettings: true },
    { type: documentTemplateTypes.statement, includePrintSettings: false },
    { type: documentTemplateTypes.installer, includePrintSettings: true },
    { type: documentTemplateTypes.jobReview, includePrintSettings: true },
    { type: documentTemplateTypes.jobSummary, includePrintSettings: true }
];

const buildExportProducts = (products) => {
    return from(products)
        .groupBy(p => p.ID)
        .select(g => {
            const productVersions = from(g.getSource()).orderBy(productVersionSort).toArray();
            const firstVersion = productVersions[0];

            if (firstVersion.IsMarketplaceProduct) {
                return [firstVersion];
            }

            return productVersions.filter(p => p.IsDraft);
        })
        .selectMany(p => p)
        .where(p => !accentUtils.isNull(p))
        .orderBy(p => p.Supplier ?? "")
        .thenBy(p => p.ProductName ?? "")
        .toArray();
};

const getVersionType = (product) => product.IsMarketplaceProduct ? "Marketplace" : "Draft";

const buildFileName = () => {
    const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\..+/, "").replace("T", "-");
    return `document-template-export-${stamp}.json`;
};

const getIDKey = (value) => accentUtils.isNull(value) ? null : `${value}`.toUpperCase();

const tryParseJson = (value, issues, context) => {
    if (accentUtils.isEmpty(value)) return null;

    try {
        return JSON.parse(value);
    } catch (err) {
        issues.push({
            severity: "warning",
            code: "TemplateParamsParseFailed",
            message: "Could not parse TemplateParams. Exporting the original string value.",
            error: getErrorMessage(err),
            context
        });

        return value;
    }
};

const exportDocumentTemplateEntity = (entity, issues) => {
    if (!accentUtils.isNull(entity.CustomDocumentID)) {
        issues.push({
            severity: "warning",
            code: "CustomDocumentReferenceSkipped",
            message: "CustomDocumentID was removed from export. Custom document import support will need a name/code based mapping.",
            documentTemplate: {
                type: entity.Type,
                name: entity.Name
            },
            _source: {
                id: entity.ID,
                customDocumentID: entity.CustomDocumentID
            }
        });
    }

    return {
        type: entity.Type,
        name: entity.Name,
        payload: {
            Name: entity.Name,
            Type: entity.Type,
            TemplateID: entity.TemplateID,
            TemplateParams: tryParseJson(entity.TemplateParams, issues, {
                documentTemplate: {
                    type: entity.Type,
                    name: entity.Name
                },
                _source: {
                    id: entity.ID
                }
            })
        },
        _source: {
            id: entity.ID
        }
    };
};

const exportPrintSettingEntity = (entity) => ({
    type: entity.Type,
    name: entity.Name,
    payload: {
        Name: entity.Name,
        Type: entity.Type
    },
    _source: {
        id: entity.ID
    }
});

const isSameGuid = (a, b) => getIDKey(a) === getIDKey(b);

const buildOptionLookup = (model) => {
    const optionByID = {};
    const valueByID = {};

    if (accentUtils.isNull(model)) {
        return { optionByID, valueByID };
    }

    const groups = configFrom(model.GetProductOptionGroups()).toArray();

    groups.forEach(group => {
        const options = configFrom(model.GetProductOptions(group.ID)).toArray();

        options.forEach(option => {
            optionByID[getIDKey(option.ID)] = {
                groupCode: group.Code,
                groupName: group.Description,
                optionCode: option.Code,
                optionName: option.Description
            };

            const values = configFrom(model.GetProductOptionValues(option.ID)).toArray();

            values.forEach(value => {
                valueByID[getIDKey(value.ID)] = {
                    groupCode: group.Code,
                    groupName: group.Description,
                    optionCode: option.Code,
                    optionName: option.Description,
                    valueCode: value.Code,
                    valueName: value.Description
                };
            });
        });
    });

    return { optionByID, valueByID };
};

const getProductExportIdentity = (product, productVersionID) => ({
    supplierName: product.Supplier,
    productCode: getProductCode(product),
    productName: product.ProductName,
    versionType: getVersionType(product),
    _source: {
        productID: product.ID,
        productVersionID,
        supplierKey: product.SupplierKey
    }
});

const getErrorMessage = (err) => err?.message ?? `${err}`;

export class ProductPrintSettingsExportDlg extends React.Component {

    constructor(props) {
        super(props);

        const exportProducts = buildExportProducts(props.products ?? []);
        const documentTemplates = props.documentTemplates ?? [];
        const printSettings = props.printSettings ?? [];

        this.state = {
            documentTemplates,
            printSettings,
            selectedDocumentTemplateKeys: documentTemplates.map(t => toKey(t.ID)),
            selectedPrintSettingKeys: printSettings.map(p => toKey(p.ID)),
            exportProducts,
            selectedProductKeys: exportProducts.map(getExportProductKey),
            supplierFilter: ""
        };

        this.onClose = this.onClose.bind(this);
        this.onExport = this.onExport.bind(this);
        this.onDocumentTypeSelectionChanged = this.onDocumentTypeSelectionChanged.bind(this);
        this.onDocumentCategorySelectionChanged = this.onDocumentCategorySelectionChanged.bind(this);
        this.onDocumentTemplateSelectionChanged = this.onDocumentTemplateSelectionChanged.bind(this);
        this.onPrintSettingSelectionChanged = this.onPrintSettingSelectionChanged.bind(this);
        this.selectOnlyDocumentTemplates = this.selectOnlyDocumentTemplates.bind(this);
        this.selectOnlyPrintSettings = this.selectOnlyPrintSettings.bind(this);
        this.selectAllTypeScope = this.selectAllTypeScope.bind(this);
        this.onSupplierFilterChanged = this.onSupplierFilterChanged.bind(this);
        this.onProductSelectionChanged = this.onProductSelectionChanged.bind(this);
        this.onSupplierSelectionChanged = this.onSupplierSelectionChanged.bind(this);
        this.selectAllVisible = this.selectAllVisible.bind(this);
        this.clearAllVisible = this.clearAllVisible.bind(this);
        this.clearSupplierFilter = this.clearSupplierFilter.bind(this);
    }

    getDocumentTypeGroups() {
        return documentTypeGroups.map(g => ({
            ...g,
            documentTemplates: from(this.state.documentTemplates).where(t => t.Type === g.type).orderBy(t => t.Name ?? "").toArray(),
            printSettings: g.includePrintSettings ? from(this.state.printSettings).where(p => p.Type === g.type).orderBy(p => p.Name ?? "").toArray() : []
        }));
    }

    getVisibleProducts() {
        const supplierFilter = this.state.supplierFilter?.toLowerCase() ?? "";

        if (accentUtils.isEmpty(supplierFilter)) {
            return this.state.exportProducts;
        }

        return this.state.exportProducts.filter(p => (p.Supplier ?? "").toLowerCase().includes(supplierFilter));
    }

    getSupplierGroups() {
        return from(this.getVisibleProducts())
            .groupBy(p => p.Supplier ?? "")
            .select(g => ({
                supplier: g.key(),
                products: from(g.getSource()).orderBy(p => p.ProductName ?? "").toArray()
            }))
            .orderBy(g => g.supplier)
            .toArray();
    }

    onClose(e) {
        if (e.promise) e.promise();
        this.props.onClose({ canceled: true });
    }

    async buildProductPrintSettingsExport(rawPrintSettings, selectedProducts, issues) {
        const productPrintSettings = [];

        for (const product of selectedProducts) {
            const productVersionID = product.VersionID ?? accentUtils.getEmptyGuid();
            const productIdentity = getProductExportIdentity(product, productVersionID);
            const memberLookups = {};
            let model = null;

            try {
                model = await loadProductModel(product.ID, productVersionID);
            } catch (err) {
                issues.push({
                    severity: "warning",
                    code: "ProductModelLoadFailed",
                    message: "Could not load product model. Product print settings for this product were skipped.",
                    error: getErrorMessage(err),
                    product: productIdentity
                });

                continue;
            }

            if (accentUtils.isNull(model)) {
                issues.push({
                    severity: "warning",
                    code: "ProductModelLoadFailed",
                    message: "Could not load product model. Product print settings for this product were skipped.",
                    product: productIdentity
                });

                continue;
            }

            const productLookup = buildOptionLookup(model);

            try {
                for (const printSetting of rawPrintSettings) {
                    const rawItems = (printSetting.Items ?? [])
                        .filter(i => isSameGuid(i.ProductID, product.ID) && isSameGuid(i.ProductVersionID, productVersionID));

                    const exportItems = [];
                    const configuredItems = rawItems.filter(i => i.ShowAs !== printSettingItemShowAs.hide);

                    for (const item of configuredItems) {
                        const itemExport = await this.buildProductPrintSettingItemExport(item, product, productVersionID, productLookup, memberLookups, issues);
                        exportItems.push(itemExport);
                    }

                    const productSettings = (printSetting.ProductSettings ?? [])
                        .find(s => isSameGuid(s.ProductID, product.ID) && isSameGuid(s.ProductVersionID, productVersionID));

                    if (exportItems.length === 0 && accentUtils.isEmpty(productSettings?.ShowEmptyRow)) {
                        continue;
                    }

                    productPrintSettings.push({
                        printSetting: {
                            type: printSetting.Type,
                            name: printSetting.Name,
                            _source: {
                                id: printSetting.ID
                            }
                        },
                        product: productIdentity,
                        productSettings: {
                            showEmptyRow: productSettings?.ShowEmptyRow
                        },
                        items: exportItems
                    });
                }
            } finally {
                removeFromModelCache(product.ID, productVersionID);
                Object.keys(memberLookups).forEach(key => {
                    const memberLookup = memberLookups[key];
                    removeFromModelCache(memberLookup.productID, memberLookup.productVersionID);
                });
            }
        }

        return productPrintSettings;
    }

    async getItemLookup(item, productLookup, memberLookups, issues) {
        if (accentUtils.isNull(item.MemberProductID)) {
            return productLookup;
        }

        const memberProductVersionID = item.MemberProductVersionID ?? accentUtils.getEmptyGuid();
        const memberKey = `${item.MemberProductID}_${memberProductVersionID}`;

        if (accentUtils.isNull(memberLookups[memberKey])) {
            try {
                const memberModel = await loadProductModel(item.MemberProductID, memberProductVersionID);

                if (accentUtils.isNull(memberModel)) {
                    throw new Error("Member product model was not found.");
                }

                memberLookups[memberKey] = {
                    productID: item.MemberProductID,
                    productVersionID: memberProductVersionID,
                    lookup: buildOptionLookup(memberModel)
                };
            } catch (err) {
                issues.push({
                    severity: "warning",
                    code: "MemberProductModelLoadFailed",
                    message: "Could not load member product model. Member product option codes may be unresolved.",
                    error: getErrorMessage(err),
                    memberProductName: this.getProductName(item.MemberProductID),
                    memberProductCode: this.getProductCode(item.MemberProductID),
                    _source: {
                        memberProductID: item.MemberProductID,
                        memberProductVersionID
                    }
                });

                memberLookups[memberKey] = {
                    productID: item.MemberProductID,
                    productVersionID: memberProductVersionID,
                    lookup: buildOptionLookup(null)
                };
            }
        }

        return memberLookups[memberKey].lookup;
    }

    async buildProductPrintSettingItemExport(item, product, productVersionID, productLookup, memberLookups, issues) {
        const itemID = item.ItemID ?? "";
        const isOptionID = guidRegex.test(itemID);
        const itemLookup = await this.getItemLookup(item, productLookup, memberLookups, issues);
        const optionMatch = isOptionID ? itemLookup.optionByID[getIDKey(itemID)] : null;
        const valueMatch = isOptionID ? itemLookup.valueByID[getIDKey(itemID)] : null;
        const configMatch = optionMatch ?? valueMatch;

        if (isOptionID && accentUtils.isNull(configMatch)) {
            issues.push({
                severity: "warning",
                code: "ProductOptionLookupFailed",
                message: "Could not resolve product print setting item to option or value codes.",
                product: getProductExportIdentity(product, productVersionID),
                memberProductName: this.getProductName(item.MemberProductID),
                memberProductCode: this.getProductCode(item.MemberProductID),
                _source: {
                    itemID: item.ItemID,
                    productID: item.ProductID,
                    productVersionID: item.ProductVersionID ?? productVersionID,
                    memberProductID: item.MemberProductID,
                    memberProductVersionID: item.MemberProductVersionID
                }
            });
        }

        const itemIdentity = isOptionID
            ? {
                itemType: valueMatch ? "ProductOptionValue" : "ProductOption",
                groupCode: configMatch?.groupCode,
                groupName: configMatch?.groupName,
                optionCode: configMatch?.optionCode,
                optionName: configMatch?.optionName,
                valueCode: valueMatch?.valueCode,
                valueName: valueMatch?.valueName,
                unresolved: accentUtils.isNull(configMatch)
            }
            : {
                itemType: "LineField",
                field: item.Field ?? itemID,
                itemCode: item.Field ?? itemID
            };

        return {
            ...itemIdentity,
            productName: product.ProductName,
            memberProductName: this.getProductName(item.MemberProductID),
            memberProductCode: this.getProductCode(item.MemberProductID),
            label: item.Label,
            showAs: item.ShowAs,
            showIfEmpty: item.ShowIfEmpty,
            sortIndex: item.SortIndex,
            source: item.Source,
            additionalSettings: this.getAdditionalSettings(item),
            _source: {
                itemID: item.ItemID,
                productID: item.ProductID,
                productVersionID: item.ProductVersionID ?? productVersionID,
                memberProductID: item.MemberProductID,
                memberProductVersionID: item.MemberProductVersionID
            }
        };
    }

    getProductName(productID) {
        if (accentUtils.isNull(productID)) return null;

        return this.state.exportProducts.find(p => isSameGuid(p.ID, productID))?.ProductName ?? null;
    }

    getProductCode(productID) {
        if (accentUtils.isNull(productID)) return null;

        const product = this.state.exportProducts.find(p => isSameGuid(p.ID, productID));
        return accentUtils.isNull(product) ? null : getProductCode(product);
    }

    getAdditionalSettings(item) {
        if (accentUtils.isNull(item.ShowFabricWidth) && accentUtils.isNull(item.ShowPatternRepeat)) {
            return null;
        }

        return {
            type: "fabric",
            showFabricWidth: item.ShowFabricWidth,
            showPatternRepeat: item.ShowPatternRepeat
        };
    }

    async buildExportFile() {
        const selectedDocumentTemplates = this.state.documentTemplates.filter(t => this.state.selectedDocumentTemplateKeys.includes(toKey(t.ID)));
        const selectedPrintSettings = this.state.printSettings.filter(p => this.state.selectedPrintSettingKeys.includes(toKey(p.ID)));
        const selectedProducts = this.state.exportProducts.filter(p => this.state.selectedProductKeys.includes(getExportProductKey(p)));
        const rawPrintSettings = [];
        const issues = [];

        selectedPrintSettings
            .filter(printSetting => accentUtils.isEmpty(printSetting.Type))
            .forEach(printSetting => {
                issues.push({
                    severity: "warning",
                    code: "PrintSettingTypeMissing",
                    message: "Print setting has no type. Import matching by type+name may not be reliable.",
                    printSetting: {
                        name: printSetting.Name
                    },
                    _source: {
                        id: printSetting.ID
                    }
                });
            });

        selectedProducts
            .filter(product => accentUtils.isEmpty(getProductCode(product)))
            .forEach(product => {
                issues.push({
                    severity: "warning",
                    code: "ProductCodeMissing",
                    message: "Product has no product code. Import matching by supplierName+productCode may not be reliable.",
                    product: getProductExportIdentity(product, product.VersionID ?? accentUtils.getEmptyGuid())
                });
            });

        for (const printSetting of selectedPrintSettings) {
            rawPrintSettings.push(await query_PrintSettings_ExportRaw.getFirstOrDefault({ id: printSetting.ID }));
        }

        const productPrintSettings = await this.buildProductPrintSettingsExport(rawPrintSettings, selectedProducts, issues);

        return {
            schema: exportSchema,
            exportedAt: new Date().toISOString(),
            matching: {
                documentTemplates: "type+name",
                printSettings: "type+name",
                products: "supplierName+productCode",
                options: "groupCode+optionCode",
                optionValues: "groupCode+optionCode+valueCode"
            },
            documentTemplates: selectedDocumentTemplates.map(t => exportDocumentTemplateEntity(t, issues)),
            printSettings: rawPrintSettings.map(exportPrintSettingEntity),
            productPrintSettings,
            issues
        };
    }

    async onExport(e) {
        const done = showWait();

        try {
            const exportFile = await this.buildExportFile();
            await downloadObject(exportFile, buildFileName());
            await done();
            if (e.promise) e.promise();
            this.props.onClose({ canceled: false, exportFile });
        } catch (err) {
            console.error(err);
            await done();
            if (e.promise) e.promise();
        }
    }

    onDocumentTypeSelectionChanged(e) {
        const typeGroup = this.getDocumentTypeGroups().find(g => g.type === e.tagName);

        if (accentUtils.isNull(typeGroup)) return;

        const documentTemplateKeys = typeGroup.documentTemplates.map(t => toKey(t.ID));
        const printSettingKeys = typeGroup.printSettings.map(p => toKey(p.ID));

        this.setState({
            selectedDocumentTemplateKeys: e.checked
                ? [...new Set([...this.state.selectedDocumentTemplateKeys, ...documentTemplateKeys])]
                : this.state.selectedDocumentTemplateKeys.filter(k => !documentTemplateKeys.includes(k)),
            selectedPrintSettingKeys: e.checked
                ? [...new Set([...this.state.selectedPrintSettingKeys, ...printSettingKeys])]
                : this.state.selectedPrintSettingKeys.filter(k => !printSettingKeys.includes(k))
        });
    }

    onDocumentCategorySelectionChanged(e) {
        const [category, type] = e.tagName.split("|");
        const typeGroup = this.getDocumentTypeGroups().find(g => g.type === type);

        if (accentUtils.isNull(typeGroup)) return;

        if (category === "templates") {
            const documentTemplateKeys = typeGroup.documentTemplates.map(t => toKey(t.ID));
            const selectedDocumentTemplateKeys = e.checked
                ? [...new Set([...this.state.selectedDocumentTemplateKeys, ...documentTemplateKeys])]
                : this.state.selectedDocumentTemplateKeys.filter(k => !documentTemplateKeys.includes(k));

            this.setState({ selectedDocumentTemplateKeys });
        }

        if (category === "printSettings") {
            const printSettingKeys = typeGroup.printSettings.map(p => toKey(p.ID));
            const selectedPrintSettingKeys = e.checked
                ? [...new Set([...this.state.selectedPrintSettingKeys, ...printSettingKeys])]
                : this.state.selectedPrintSettingKeys.filter(k => !printSettingKeys.includes(k));

            this.setState({ selectedPrintSettingKeys });
        }
    }

    onDocumentTemplateSelectionChanged(e) {
        const selectedDocumentTemplateKeys = e.checked
            ? [...new Set([...this.state.selectedDocumentTemplateKeys, e.tagName])]
            : this.state.selectedDocumentTemplateKeys.filter(k => k !== e.tagName);

        this.setState({ selectedDocumentTemplateKeys });
    }

    onPrintSettingSelectionChanged(e) {
        const selectedPrintSettingKeys = e.checked
            ? [...new Set([...this.state.selectedPrintSettingKeys, e.tagName])]
            : this.state.selectedPrintSettingKeys.filter(k => k !== e.tagName);

        this.setState({ selectedPrintSettingKeys });
    }

    selectOnlyDocumentTemplates() {
        this.setState({
            selectedDocumentTemplateKeys: this.state.documentTemplates.map(t => toKey(t.ID)),
            selectedPrintSettingKeys: []
        });
    }

    selectOnlyPrintSettings() {
        this.setState({
            selectedDocumentTemplateKeys: [],
            selectedPrintSettingKeys: this.state.printSettings.map(p => toKey(p.ID))
        });
    }

    selectAllTypeScope() {
        this.setState({
            selectedDocumentTemplateKeys: this.state.documentTemplates.map(t => toKey(t.ID)),
            selectedPrintSettingKeys: this.state.printSettings.map(p => toKey(p.ID))
        });
    }

    onSupplierFilterChanged(value) {
        this.setState({ supplierFilter: value ?? "" });
    }

    clearSupplierFilter() {
        this.setState({ supplierFilter: "" });
    }

    onProductSelectionChanged(e) {
        const productKey = e.tagName;
        const selectedProductKeys = e.checked
            ? [...new Set([...this.state.selectedProductKeys, productKey])]
            : this.state.selectedProductKeys.filter(k => k !== productKey);

        this.setState({ selectedProductKeys });
    }

    onSupplierSelectionChanged(e) {
        const supplier = e.tagName;
        const supplierProductKeys = this.state.exportProducts
            .filter(p => (p.Supplier ?? "") === supplier)
            .map(getExportProductKey);

        const selectedProductKeys = e.checked
            ? [...new Set([...this.state.selectedProductKeys, ...supplierProductKeys])]
            : this.state.selectedProductKeys.filter(k => !supplierProductKeys.includes(k));

        this.setState({ selectedProductKeys });
    }

    selectAllVisible() {
        const visibleProductKeys = this.getVisibleProducts().map(getExportProductKey);
        const selectedProductKeys = [...new Set([...this.state.selectedProductKeys, ...visibleProductKeys])];

        this.setState({ selectedProductKeys });
    }

    clearAllVisible() {
        const visibleProductKeys = this.getVisibleProducts().map(getExportProductKey);
        const selectedProductKeys = this.state.selectedProductKeys.filter(k => !visibleProductKeys.includes(k));

        this.setState({ selectedProductKeys });
    }

    render() {
        const documentGroups = this.getDocumentTypeGroups();
        const supplierGroups = this.getSupplierGroups();
        const selectedDocumentItemCount = this.state.selectedDocumentTemplateKeys.length + this.state.selectedPrintSettingKeys.length;
        const selectedProductCount = this.state.selectedProductKeys.length;

        const documentRows = documentGroups.map(g => {
            const documentTemplateKeys = g.documentTemplates.map(t => toKey(t.ID));
            const printSettingKeys = g.printSettings.map(p => toKey(p.ID));
            const typeKeys = [...documentTemplateKeys, ...printSettingKeys];
            const typeSelectedCount = documentTemplateKeys.filter(k => this.state.selectedDocumentTemplateKeys.includes(k)).length + printSettingKeys.filter(k => this.state.selectedPrintSettingKeys.includes(k)).length;
            const isTypeSelected = typeKeys.length > 0 && typeSelectedCount === typeKeys.length;

            const templatesSelectedCount = documentTemplateKeys.filter(k => this.state.selectedDocumentTemplateKeys.includes(k)).length;
            const isTemplatesSelected = documentTemplateKeys.length > 0 && templatesSelectedCount === documentTemplateKeys.length;
            const printSettingsSelectedCount = printSettingKeys.filter(k => this.state.selectedPrintSettingKeys.includes(k)).length;
            const isPrintSettingsSelected = printSettingKeys.length > 0 && printSettingsSelectedCount === printSettingKeys.length;

            const templateRows = g.documentTemplates.map(template => {
                const templateKey = toKey(template.ID);
                const isSelected = this.state.selectedDocumentTemplateKeys.includes(templateKey);

                return <tr key={`template_${templateKey}`}>
                    <td style={{ width: "35px" }}></td>
                    <td>
                        <div className="d-flex align-items-center" style={{ paddingLeft: "55px", gap: "5px" }}>
                            <AccentCheckUnbound
                                key={`template_${templateKey}_${isSelected}`}
                                tagName={templateKey}
                                defaultValue={isSelected}
                                onChange={this.onDocumentTemplateSelectionChanged}
                            />
                            <span>{template.Name}</span>
                        </div>
                    </td>
                </tr>;
            });

            const printSettingRows = g.printSettings.map(printSetting => {
                const printSettingKey = toKey(printSetting.ID);
                const isSelected = this.state.selectedPrintSettingKeys.includes(printSettingKey);

                return <tr key={`printSetting_${printSettingKey}`}>
                    <td style={{ width: "35px" }}></td>
                    <td>
                        <div className="d-flex align-items-center" style={{ paddingLeft: "55px", gap: "5px" }}>
                            <AccentCheckUnbound
                                key={`printSetting_${printSettingKey}_${isSelected}`}
                                tagName={printSettingKey}
                                defaultValue={isSelected}
                                onChange={this.onPrintSettingSelectionChanged}
                            />
                            <span>{printSetting.Name}</span>
                        </div>
                    </td>
                </tr>;
            });

            return <React.Fragment key={g.type}>
                <tr style={{ backgroundColor: "#f7f7f7" }}>
                    <td style={{ width: "35px" }}>
                        <AccentCheckUnbound
                            key={`type_${g.type}_${isTypeSelected}_${typeSelectedCount}`}
                            tagName={g.type}
                            defaultValue={isTypeSelected}
                            onChange={this.onDocumentTypeSelectionChanged}
                        />
                    </td>
                    <td>
                        <span style={{ fontWeight: "bolder" }}>{t(g.type)}</span>
                        <Badge color="secondary" style={{ marginLeft: "8px" }}>{typeSelectedCount} / {typeKeys.length}</Badge>
                    </td>
                </tr>
                <tr>
                    <td style={{ width: "35px" }}></td>
                    <td>
                        <div className="d-flex align-items-center" style={{ paddingLeft: "28px", gap: "5px" }}>
                            <AccentCheckUnbound
                                key={`templates_${g.type}_${isTemplatesSelected}_${templatesSelectedCount}`}
                                tagName={`templates|${g.type}`}
                                defaultValue={isTemplatesSelected}
                                disabled={documentTemplateKeys.length === 0}
                                onChange={this.onDocumentCategorySelectionChanged}
                            />
                            <span style={{ fontWeight: "bolder" }}>Templates</span>
                            <Badge color="secondary">{templatesSelectedCount} / {documentTemplateKeys.length}</Badge>
                        </div>
                    </td>
                </tr>
                {templateRows}
                {g.includePrintSettings && <tr>
                    <td style={{ width: "35px" }}></td>
                    <td>
                        <div className="d-flex align-items-center" style={{ paddingLeft: "28px", gap: "5px" }}>
                            <AccentCheckUnbound
                                key={`printSettings_${g.type}_${isPrintSettingsSelected}_${printSettingsSelectedCount}`}
                                tagName={`printSettings|${g.type}`}
                                defaultValue={isPrintSettingsSelected}
                                disabled={printSettingKeys.length === 0}
                                onChange={this.onDocumentCategorySelectionChanged}
                            />
                            <span style={{ fontWeight: "bolder" }}>Print Settings</span>
                            <Badge color="secondary">{printSettingsSelectedCount} / {printSettingKeys.length}</Badge>
                        </div>
                    </td>
                </tr>}
                {printSettingRows}
            </React.Fragment>;
        });

        const supplierRows = supplierGroups.map(g => {
            const supplierProductKeys = g.products.map(getExportProductKey);
            const selectedSupplierProductCount = supplierProductKeys.filter(k => this.state.selectedProductKeys.includes(k)).length;
            const isSupplierSelected = supplierProductKeys.length > 0 && selectedSupplierProductCount === supplierProductKeys.length;

            const productRows = g.products.map(p => {
                const productKey = getExportProductKey(p);
                const isSelected = this.state.selectedProductKeys.includes(productKey);

                return <tr key={productKey}>
                    <td style={{ width: "35px" }}></td>
                    <td>
                        <div className="d-flex align-items-center" style={{ paddingLeft: "28px", gap: "5px" }}>
                            <AccentCheckUnbound
                                key={`${productKey}_${isSelected}`}
                                tagName={productKey}
                                defaultValue={isSelected}
                                onChange={this.onProductSelectionChanged}
                            />
                            <div>
                                <span style={{ fontWeight: "bolder" }}>{p.ProductName}</span>
                                <span style={{ paddingLeft: "5px", opacity: 0.7 }}>{getProductCode(p)}</span>
                            </div>
                        </div>
                    </td>
                    <td>
                        <Badge color={p.IsMarketplaceProduct ? "info" : "warning"}>
                            {p.IsMarketplaceProduct ? "Marketplace" : "Draft"}
                        </Badge>
                    </td>
                </tr>;
            });

            return <React.Fragment key={g.supplier}>
                <tr style={{ backgroundColor: "#f7f7f7" }}>
                    <td style={{ width: "35px" }}>
                        <AccentCheckUnbound
                            key={`${g.supplier}_${isSupplierSelected}_${selectedSupplierProductCount}`}
                            tagName={g.supplier}
                            defaultValue={isSupplierSelected}
                            onChange={this.onSupplierSelectionChanged}
                        />
                    </td>
                    <td>
                        <span style={{ fontWeight: "bolder" }}>{accentUtils.isEmpty(g.supplier) ? "No Supplier" : g.supplier}</span>
                        <Badge color="secondary" style={{ marginLeft: "8px" }}>{selectedSupplierProductCount} / {supplierProductKeys.length}</Badge>
                    </td>
                    <td>
                        <Badge color="secondary">Supplier</Badge>
                    </td>
                </tr>
                {productRows}
            </React.Fragment>;
        });

        return (<AccentDialog
            headerContent="Export Document Templates / Print Settings"
            onClose={this.onClose}
            relativeWidth={d => d.width - 80}
            relativeHeight={d => d.height - 80}
            actionsContent={[
                <AccentButton key="export" tagName="exportPrintSettingsBtn" disabled={selectedDocumentItemCount === 0} onClick={this.onExport}>
                    {t("application_strings.application.buttons.export")}
                </AccentButton>
            ]}
        >
            <Row>
                <Col md={6}>
                    <h5>Type Scope</h5>
                </Col>
                <Col md={6}>
                    <h5>Product Scope</h5>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <div className="d-flex align-items-end" style={{ gap: "5px", height: "58px", paddingBottom: "10px" }}>
                        <AccentButton onClick={this.selectAllTypeScope}>All</AccentButton>
                        <AccentButton onClick={this.selectOnlyDocumentTemplates}>Templates Only</AccentButton>
                        <AccentButton onClick={this.selectOnlyPrintSettings}>Print Settings Only</AccentButton>
                    </div>
                </Col>
                <Col md={6}>
                    <Row style={{ height: "58px" }}>
                        <Col md={6}>
                            <AccentTextUnbound
                                label="Supplier"
                                defaultValue={this.state.supplierFilter}
                                placeHolder="Filter by supplier name"
                                onChange={this.onSupplierFilterChanged}
                                resetWhenDefaultValueChanges
                                buttons={[
                                    <AccentButton key="clearSupplierFilter" disabled={accentUtils.isEmpty(this.state.supplierFilter)} onClick={this.clearSupplierFilter}>Clear</AccentButton>
                                ]}
                            />
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-end" style={{ gap: "5px", paddingTop: "24px", paddingBottom: "10px" }}>
                                <AccentButton onClick={this.selectAllVisible}>Select All</AccentButton>
                                <AccentButton onClick={this.clearAllVisible}>Clear All</AccentButton>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <div style={{ opacity: 0.7, paddingTop: "10px", paddingBottom: "10px" }}>
                        {selectedDocumentItemCount} selected
                    </div>
                </Col>
                <Col md={6}>
                    <div style={{ opacity: 0.7, paddingTop: "10px", paddingBottom: "10px" }}>
                        {selectedProductCount} selected
                    </div>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <table className="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Document Type / Name</th>
                            </tr>
                        </thead>
                        <tbody>
                            {documentRows}
                        </tbody>
                    </table>
                </Col>
                <Col md={6}>
                    <table className="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Product</th>
                                <th>Export Version</th>
                            </tr>
                        </thead>
                        <tbody>
                            {supplierRows}
                            {supplierRows.length === 0 && <tr><td colSpan={3}>No products found.</td></tr>}
                        </tbody>
                    </table>
                </Col>
            </Row>
        </AccentDialog>);
    }
}
